import React, { useRef, useCallback, useMemo, useEffect, useState, useContext } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import { formatDate } from "common/calendar";
import serveRequestErrors from "common/serve-request-errors";
import { formatCurrency } from "common/format-currency-pattern";
import { AxiosContext } from "contexts/with-interceptor-provider";
import AppStatus from "components/app-status";
import AppEmptyState from "components/app-empty-state";
import AppClaimModal from "components/pages/claim-management/app-claim-modal";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import AppConfirmDeleteModal from "components/pages/claim-management/app-confirm-delete-claim";
import pdfIcon from "assets/images/pdf-icon.svg";
import viewIcon from "assets/images/view-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";

const AppClaimHistory = (props) => {
	const { id } = useParams();
	const claimModalRef = useRef();
	const confirmDeleteRef = useRef();
	const rejectReasonRef = useRef();
	const context = useContext(AxiosContext);
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedClaim, setSelectedClaim] = useState({});
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const paramsRef = useRef({ statusListStr: searchParams.get("statusListStr") || "CANCELLED,APPROVED,REJECTED", page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "", tab: "Claim History" });
	const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status, [data.status]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.claims.list(sanitizeObject({ ...payload, id }));
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}
	}, [id]);

	//prettier-ignore
	const onHandlePagination = useCallback((event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	}, [onHandleGetList, data.total]);

	//prettier-ignore
	const onHandleSort = useCallback((id, order) => {
		paramsRef.current.sort = order ? id + "," + order : "";
	
		paramsRef.current.page = 1;
	
		onHandleGetList();
	}, [onHandleGetList]);

	const onHandleRemove = () => {
		setAnchorEl(null);
		confirmDeleteRef.current.onHandleShow(selectedClaim.id);
	};

	const onHandleView = () => {
		setAnchorEl(null);
		claimModalRef.current.onHandleShow(selectedClaim.id);
	};

	const onHandleCloseMenu = () => {
		setAnchorEl(null);
	};

	const onHandleTableMenu = (event, { row }) => {
		setSelectedClaim(row.original);
		setAnchorEl(event.currentTarget);
	};

	const SubmitDate = useCallback(({ row }) => {
		const date = formatDate(row.original.createdDate);
		return <AppTableCell left value={date} />;
	}, []);

	const AmountCell = useCallback(({ row }) => <AppTableCell left value={formatCurrency(row.original.claimValue)} />, []);

	//prettier-ignore
	const AttachmentCell = useCallback(({ row }) => {
		const file = row.original?.companyClaimApplicationFiles?.[0];
		const value = file ? (<a href={file?.fileURL} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attacment" /></a>) : ("-");
		return (
			<AppTableCell center value={value} />
		);
	}, []);

	//prettier-ignore
	const EInvoiceExemptedCell = useCallback(({ row }) => <AppTableCell center value={row.original.eInvoiceExempted ? "Yes" : "No"} />, []);

	//prettier-ignore
	const MenuCell = useCallback(( obj ) => {
		return <AppTableMoreIcon onClick={(event) => onHandleTableMenu(event, obj)} />;
	}, []);

	//prettier-ignore
	const StatusCell = useCallback(({ row }) => {
		return <AppStatus status={row.values.status} />;
	}, []);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Claim Type",
			accessor: "claimTypeMapped",
			disableSortBy: false,
		},
		{
			Header: "Submit Date",
			accessor: "createdDate",
			disableSortBy: false,
			Cell: SubmitDate,
		},
		{
			Header: () => <div className="table__amount">Amount (RM)</div>,
			id: "claimValue",
			accessor: "companyClaimApplicationDetails",
			disableSortBy: false,
            Cell: AmountCell
		},
		{
            Header: () => <div className="table__attachment">Attachment</div>,
			accessor: "companyClaimApplicationFiles",
			disableSortBy: true,
			Cell: AttachmentCell
		},
		{
            Header: () => <div className="table__e-invoice-exempted">E-Invoice Exempted</div>,
			accessor: "eInvoiceExempted",
			disableSortBy: true,
			Cell: EInvoiceExemptedCell
		},
		{
			Header: "Status",
			accessor: "status",
			disableSortBy: false,
			Cell: StatusCell,
		},
		{
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: MenuCell,
		}
	], [MenuCell, StatusCell, AttachmentCell, AmountCell, SubmitDate, EInvoiceExemptedCell]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		return () => {
			context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.CLAIMS.LIST);
		};
	}, [context]);

	return (
		<div className="app-claim-history">
			{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}
			{!isEmptyState && <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />}

			{/* prettier-ignore */}
			<Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onHandleCloseMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <MenuItem onClick={onHandleView}><img className="app-table-menu__icon" src={viewIcon} alt="view" />View</MenuItem>
                {selectedClaim?.status === COMMON.STATUS_ID.APPROVED && (<MenuItem onClick={onHandleRemove}><img className="app-table-menu__icon" src={removeIcon} alt="view" />Cancel</MenuItem>)}
            </Menu>

			<AppConfirmDeleteModal ref={confirmDeleteRef} onHandleGetList={onHandleGetList} />

			<AppClaimModal ref={claimModalRef} onHandleGetList={onHandleGetList} rejectReasonRef={rejectReasonRef} showActions={false} />
		</div>
	);
};

export default AppClaimHistory;
